import { FC, useState } from 'react'
import { Search, X } from 'lucide-react'
import { Input } from '../ui/input'
import { WorkspaceList } from './WorkspaceList'
import { cn } from '../../lib/utils'
import type { WorkspaceSummary } from '../../services/workspaceService'

interface SidebarContentProps {
  workspaces: WorkspaceSummary[]
  activeWorkspaceId: string | null
  onWorkspaceSelect: (id: string) => void
  onRename?: (id: string, newName: string) => void
  onClose?: (id: string) => void
  onDelete?: (id: string) => void
  isLoading?: boolean
}

/**
 * Sidebar content: search bar + grouped workspace list.
 * No project section -- workspaces are the top-level organizational unit.
 */
export const SidebarContent: FC<SidebarContentProps> = ({
  workspaces,
  activeWorkspaceId,
  onWorkspaceSelect,
  onRename,
  onClose,
  onDelete,
  isLoading,
}) => {
  const [searchTerm, setSearchTerm] = useState('')

  return (
    <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
      {/* Search */}
      <div className="px-3 pt-2 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            placeholder="Search workspaces..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={cn(
              'pl-9 pr-8 h-8 text-sm',
              'bg-surface border-border/50',
              'placeholder:text-muted-foreground/60',
              'focus:border-primary/50 focus:ring-1 focus:ring-primary/20',
              'transition-all duration-150'
            )}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Grouped workspace list */}
      <WorkspaceList
        workspaces={workspaces}
        activeWorkspaceId={activeWorkspaceId}
        searchTerm={searchTerm}
        onWorkspaceSelect={onWorkspaceSelect}
        onRename={onRename}
        onClose={onClose}
        onDelete={onDelete}
        isLoading={isLoading}
      />
    </div>
  )
}
